import React, { useState, useEffect } from "react";
import api from "../config/api";
import "./Dashboard.css";
import {
  HiCode,
  HiUserGroup,
  HiClipboardList,
  HiChartBar,
} from "react-icons/hi";
import { HiDocumentText, HiBuildingOffice2 } from "react-icons/hi2";
import MetricCard, { type CardMetric } from "../components/MetricCard";
import { toast } from "react-toastify";
import { syncOutcomes } from "../utils/syncSummary";
import { errorMessage } from "../utils/errors";
import { syncEntity } from "../utils/syncEntity";
import { APP_VERSION } from "../utils/version";
import { useChangesContext } from "../contexts/ChangesContext";
import { ALL_CONTRACT_STATUSES_PARAM } from "../hooks/useContractFilters";
import { MdSportsRugby } from "react-icons/md";

interface DashboardStats {
  totalCoders: number;
  codersWithoutEnterprise: number;
  totalSupervisors: number;
  supervisorsWithoutEnterprise: number;
  totalEnterprises: number;
  enterprisesMissingMail: number;
  enterprisesWithoutCoder: number;
  totalGames: number;
  totalCodings: number;
  lateCodings: number;
  totalSupervisions: number;
  lateSupervisions: number;
  totalContracts: number;
  pendingContracts: number;
  totalChanges: number;
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalCoders: 0,
    codersWithoutEnterprise: 0,
    totalSupervisors: 0,
    supervisorsWithoutEnterprise: 0,
    totalEnterprises: 0,
    enterprisesMissingMail: 0,
    enterprisesWithoutCoder: 0,
    totalGames: 0,
    totalCodings: 0,
    lateCodings: 0,
    totalSupervisions: 0,
    lateSupervisions: 0,
    totalContracts: 0,
    pendingContracts: 0,
    totalChanges: 0,
  });
  const [loading, setLoading] = useState(true);
  /**
   * Which section is currently synchronising, if any.
   *
   * A flag per section rather than one shared boolean: each button has to say whether
   * it is the one running. Both are disabled while either runs — the two sets overlap
   * (a coding belongs to a coder) and letting them run at once would have them writing
   * the same rows.
   */
  const [syncingSection, setSyncingSection] = useState<
    "subcontractors" | "codings" | null
  >(null);

  /**
   * The count of changes still to handle, and the signal that it moved.
   *
   * Read from the context rather than counted here, so the card and the nav badge are
   * the same number by construction instead of two figures polling separately.
   */
  const { pendingCount, refreshTrigger } = useChangesContext();

  // Re-read on mount, and again whenever something says the changes moved — applying
  // one on the Modifications page, or a sync recording new ones.
  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  /**
   * `silent` refreshes the figures in place: no skeletons. Used after a sync, when the
   * page is already on screen and only the numbers need to catch up.
   */
  const fetchDashboardData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);

      // Fetch data from multiple endpoints
      const [
        codersRes,
        codersWithoutEnterpriseRes,
        supervisorsRes,
        supervisorsWithoutEnterpriseRes,
        enterprisesRes,
        enterprisesMissingMailRes,
        enterprisesWithoutCoderRes,
        gamesRes,
        codingsRes,
        lateCodingsRes,
        supervisionsRes,
        lateSupervisionsRes,
        contractsRes,
        pendingContractsRes,
        coderChangesRes,
        supervisorChangesRes,
      ] = await Promise.all([
        api.get("/api/coders"),
        // `limit=1`: the figure comes from `pagination.total`, so the rows themselves
        // are not needed. The same filter the card's link carries, so the number and
        // the page it leads to cannot disagree.
        api.get("/api/coders?withoutEnterprise=true&limit=1"),
        api.get("/api/supervisors"),
        api.get("/api/supervisors?withoutEnterprise=true&limit=1"),
        // `limit=1`: only the total is wanted, which the endpoint returns alongside
        // the page.
        api.get("/api/enterprises?limit=1"),
        api.get("/api/enterprises?missingMail=true&limit=1"),
        api.get("/api/enterprises?withoutCoder=true&limit=1"),
        // `limit=1` throughout: only the totals are read, and these three tables are
        // the large ones — the games list alone is several thousand rows the page
        // would download and never show.
        api.get("/api/games?limit=1"),
        api.get("/api/codings?limit=1"),
        // The same filter the card's link carries, so the figure and the page it
        // leads to cannot disagree.
        api.get("/api/codings?lateAssignment=true&limit=1"),
        api.get("/api/supervisions?limit=1"),
        api.get("/api/supervisions?lateAssignment=true&limit=1"),
        api.get("/api/contracts?limit=1"),
        // The same filter the card's link carries, counted by the API rather than over
        // the rows of one page: the figure below used to filter `data` — the first 50
        // contracts — and called the result a total.
        api.get("/api/contracts?contractStatus=pending&limit=1"),
        // `limit=1`: only the counts are wanted, which both endpoints return alongside
        // the page. Both are read, and summed, because the Modifications page merges
        // them — and so does the nav badge, which this card has to agree with.
        api.get("/api/coder-changes?limit=1"),
        api.get("/api/supervisor-changes?limit=1"),
      ]);

      // Extract totals from paginated responses
      const totalCoders = codersRes.data.pagination?.total || 0;
      const codersWithoutEnterprise =
        codersWithoutEnterpriseRes.data.pagination?.total || 0;
      const totalSupervisors = supervisorsRes.data.pagination?.total || 0;
      const supervisorsWithoutEnterprise =
        supervisorsWithoutEnterpriseRes.data.pagination?.total || 0;
      const totalEnterprises = enterprisesRes.data.pagination?.total || 0;
      const enterprisesMissingMail =
        enterprisesMissingMailRes.data.pagination?.total || 0;
      const enterprisesWithoutCoder =
        enterprisesWithoutCoderRes.data.pagination?.total || 0;
      // `total_items` rather than `total`: the games endpoint paginates in its own
      // shape, unlike the others.
      const totalGames = gamesRes.data.pagination?.total_items || 0;
      const totalCodings = codingsRes.data.pagination?.total || 0;
      const lateCodings = lateCodingsRes.data.pagination?.total || 0;
      const totalSupervisions = supervisionsRes.data.pagination?.total || 0;
      const lateSupervisions = lateSupervisionsRes.data.pagination?.total || 0;
      const totalContracts = contractsRes.data.pagination?.total || 0;
      const pendingContracts = pendingContractsRes.data.pagination?.total || 0;

      // Both sides summed: the Modifications page merges them, so its card counts them
      // together too. The pending figure is not read here — it comes from the context,
      // which the nav badge reads as well.
      const totalChanges =
        (coderChangesRes.data.pagination?.total || 0) +
        (supervisorChangesRes.data.pagination?.total || 0);

      setStats({
        totalCoders,
        codersWithoutEnterprise,
        totalSupervisors,
        supervisorsWithoutEnterprise,
        totalEnterprises,
        enterprisesMissingMail,
        enterprisesWithoutCoder,
        totalGames,
        totalCodings,
        lateCodings,
        totalSupervisions,
        lateSupervisions,
        totalContracts,
        pendingContracts,
        totalChanges,
      });
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  /**
   * Announces one entity's sync outcome and says whether it worked.
   *
   * Every toast is named: a button drives several syncs, and an unlabelled
   * "1 mis à jour" would not say which entity it counted. A request that came back at
   * all can still carry `success: false` — the sync ran and failed — so the payload is
   * what decides, not the HTTP call.
   */
  const announceSync = (label: string, data: any): boolean => {
    if (!data.success) {
      toast.error(
        `${label} : ${data.message ?? "échec de la synchronisation"}`,
      );
      return false;
    }

    const outcomes = syncOutcomes(data);
    toast.success(
      `${label} : ${outcomes.length ? outcomes.join(", ") : "aucun changement"}`,
    );
    return true;
  };

  /**
   * Synchronises the sous-traitants: coders and supervisors.
   *
   * Both run at once — neither reads what the other writes — and `allSettled` rather
   * than `all` so one failing does not bury the other's result.
   */
  const syncSubcontractors = async () => {
    const targets = [
      { label: "Codeurs", entity: "coders" as const },
      { label: "Superviseurs", entity: "supervisors" as const },
    ];

    try {
      setSyncingSection("subcontractors");
      const results = await Promise.allSettled(
        targets.map((target) => syncEntity(target.entity)),
      );

      results.forEach((result, index) => {
        const { label } = targets[index];

        if (result.status === "rejected") {
          toast.error(`${label} : ${errorMessage(result.reason)}`);
          return;
        }

        announceSync(label, result.value);
      });

      // The figures move with the sync — refreshed in place, without the skeletons.
      await fetchDashboardData(true);
    } finally {
      setSyncingSection(null);
    }
  };

  /**
   * Synchronises the codages and their supervisions, matches first.
   *
   * Sequential and in that order because each step reads what the previous one wrote:
   * a codage belongs to a match, a supervision to a codage. Running them together
   * would have codages arriving before the matches they hang off.
   *
   * A failure stops the run rather than carrying on with the next entity. The codings
   * sync is the only thing that generates contracts automatically, and letting it
   * work over a half-imported set of matches would create them against incomplete
   * data — the skipped steps are named so the run does not look complete.
   */
  const syncCodingsAndSupervisions = async () => {
    const targets = [
      { label: "Matchs", entity: "games" as const },
      { label: "Codages", entity: "codings" as const },
      { label: "Supervisions", entity: "supervisions" as const },
    ];

    try {
      setSyncingSection("codings");

      for (let index = 0; index < targets.length; index++) {
        const { label, entity } = targets[index];
        let succeeded = false;

        try {
          succeeded = announceSync(label, await syncEntity(entity));
        } catch (error) {
          toast.error(`${label} : ${errorMessage(error)}`);
        }

        if (!succeeded) {
          const skipped = targets
            .slice(index + 1)
            .map((target) => target.label);
          if (skipped.length) {
            toast.warning(
              `${skipped.join(" et ")} : synchronisation abandonnée`,
            );
          }
          break;
        }
      }

      await fetchDashboardData(true);
    } finally {
      setSyncingSection(null);
    }
  };

  // Sous-traitants: every card now shows its figures as the links.
  const coderMetrics: CardMetric[] = [
    {
      label: "Total",
      value: stats.totalCoders,
      to: "/coders",
      title: "Voir tous les codeurs",
    },
    {
      label: "Sans entreprise",
      value: stats.codersWithoutEnterprise,
      to: "/coders?withoutEnterprise=true",
      title: "Voir les codeurs rattachés à aucune entreprise",
      tone: "warning",
    },
  ];

  const supervisorMetrics: CardMetric[] = [
    {
      label: "Total",
      value: stats.totalSupervisors,
      to: "/supervisors",
      title: "Voir tous les superviseurs",
    },
    {
      label: "Sans entreprise",
      value: stats.supervisorsWithoutEnterprise,
      to: "/supervisors?withoutEnterprise=true",
      title: "Voir les superviseurs rattachés à aucune entreprise",
      tone: "warning",
    },
  ];

  const enterpriseMetrics: CardMetric[] = [
    {
      label: "Total",
      value: stats.totalEnterprises,
      to: "/enterprises",
      title: "Voir toutes les entreprises",
    },
    {
      label: "Sans email",
      value: stats.enterprisesMissingMail,
      to: "/enterprises?missingMail=true",
      title:
        "Voir les entreprises censées recevoir des contrats mais sans adresse email",
      tone: "warning",
    },
    {
      label: "Sans codeur",
      value: stats.enterprisesWithoutCoder,
      to: "/enterprises?withoutCoder=true",
      title: "Voir les entreprises auxquelles aucun codeur n'est rattaché",
      tone: "warning",
    },
  ];

  const gameMetrics: CardMetric[] = [
    {
      label: "Total",
      value: stats.totalGames,
      to: "/games",
      title: "Voir tous les matchs",
    },
  ];

  const codingMetrics: CardMetric[] = [
    {
      label: "Total",
      value: stats.totalCodings,
      to: "/codings",
      title: "Voir tous les codages",
    },
    {
      label: "Affectation en retard",
      value: stats.lateCodings,
      to: "/codings?lateAssignment=true",
      title:
        "Voir les codages sans codeur dont le match se joue dans les 7 jours ou s'est joué il y a moins de 14 jours",
      tone: "warning",
    },
  ];

  const supervisionMetrics: CardMetric[] = [
    {
      label: "Total",
      value: stats.totalSupervisions,
      to: "/supervisions",
      title: "Voir toutes les supervisions",
    },
    {
      label: "Affectation en retard",
      value: stats.lateSupervisions,
      to: "/supervisions?lateAssignment=true",
      title:
        "Voir les supervisions sans superviseur dont le match se joue dans les 7 jours ou s'est joué il y a moins de 14 jours",
      tone: "warning",
    },
  ];

  const contractMetrics: CardMetric[] = [
    {
      label: "Total",
      value: stats.totalContracts,
      // `contractStatus=all` rather than the bare page: its default filters hide the
      // cancelled, refused and paid contracts this figure counts.
      to: `/contracts?contractStatus=${ALL_CONTRACT_STATUSES_PARAM}`,
      title: "Voir tous les contrats",
    },
    {
      label: "À traiter",
      value: stats.pendingContracts,
      to: "/contracts?contractStatus=pending",
      title:
        "Voir les contrats qui n'ont pas encore été envoyés à la signature",
      tone: "warning",
    },
  ];

  const changeMetrics: CardMetric[] = [
    {
      label: "Total",
      value: stats.totalChanges,
      // `state=all` rather than the bare page, which opens on the ones still to handle.
      to: "/changes?state=all",
      title: "Voir toutes les modifications d'affectation",
    },
    {
      label: "À traiter",
      value: pendingCount,
      to: "/changes?state=pending",
      title: "Voir les modifications qui attendent encore une décision",
      tone: "warning",
    },
  ];

  if (loading) {
    return (
      <div className="dashboard">
        <div className="container">
          <div className="hero-section">
            <div className="skeleton skeleton-title"></div>
            <div className="skeleton skeleton-description"></div>
          </div>
          <div className="stats-grid">
            {Array.from(
              // Three Sous-traitants cards, three for Codages et supervisions, two
              // for Contrats.
              { length: 8 },
              (_, index) => (
                <div key={index} className="skeleton-card">
                  <div className="skeleton skeleton-icon"></div>
                  <div className="skeleton skeleton-card-title"></div>
                  <div className="skeleton skeleton-card-value"></div>
                </div>
              ),
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="container">
        <div className="hero-section">
          <h1 className="hero-title animate-fade-in-up">
            Service de gestion des contrats de codage
          </h1>
        </div>

        <div className="dashboard-sections">
          <div className="dashboard-section animate-fade-in-up">
            <div className="section-header">
              <h2 className="section-title">Sous traitants</h2>
              <button
                type="button"
                className="sync-button"
                onClick={syncSubcontractors}
                disabled={syncingSection !== null}
              >
                {syncingSection === "subcontractors"
                  ? "Synchronisation..."
                  : "Synchroniser"}
              </button>
            </div>
            <div className="stats-grid">
              <MetricCard
                title="Codeurs"
                icon={HiUserGroup}
                metrics={coderMetrics}
              />
              <MetricCard
                title="Superviseurs"
                icon={HiUserGroup}
                metrics={supervisorMetrics}
              />
              <MetricCard
                title="Entreprises"
                icon={HiBuildingOffice2}
                metrics={enterpriseMetrics}
              />
            </div>
          </div>

          <div className="dashboard-section animate-fade-in-up">
            <div className="section-header">
              <h2 className="section-title">Codages et supervisions</h2>
              <button
                type="button"
                className="sync-button"
                onClick={syncCodingsAndSupervisions}
                disabled={syncingSection !== null}
              >
                {syncingSection === "codings"
                  ? "Synchronisation..."
                  : "Synchroniser"}
              </button>
            </div>
            <div className="stats-grid">
              <MetricCard
                title="Matchs"
                icon={MdSportsRugby}
                metrics={gameMetrics}
              />
              <MetricCard
                title="Codages"
                icon={HiCode}
                metrics={codingMetrics}
              />
              <MetricCard
                title="Supervisions"
                icon={HiClipboardList}
                metrics={supervisionMetrics}
              />
            </div>
          </div>

          {/* Its own row rather than a third column: what it counts follows from the
              two sections above, contracts being generated by the codings sync and
              modifications recorded by it. */}
          <div className="dashboard-section dashboard-section-full animate-fade-in-up">
            <div className="section-header">
              <h2 className="section-title">Contrats</h2>
            </div>
            <div className="stats-grid">
              <MetricCard
                title="Contrats"
                icon={HiDocumentText}
                metrics={contractMetrics}
              />
              <MetricCard
                title="Modifications"
                icon={HiChartBar}
                metrics={changeMetrics}
              />
            </div>
          </div>
        </div>

        <p className="dashboard-version">{APP_VERSION}</p>
      </div>
    </div>
  );
};

export default Dashboard;
